import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams, useSearchParams } from 'react-router';
import { PlacementType, } from '@amzn/handshake-painter-typescript-client/v1';
import { HandshakePainterServiceApiV1 } from 'src/api/handshakeApi';
import { useDefaultApiParams } from './useDefaultApiParams';
import { resolveStorefront, parseCategories } from 'src/pages/storefrontPage/utils';
import { useAnalyticsContext } from 'src/common/metrics';
/**
 * Resolves storefront metadata and fetches products.
 * - REGULAR storefronts → product widget API (ASIN-based)
 * - DEALS storefronts   → storefront API (strategy + category)
 */
export const useStorefront = (options) => {
    var _a;
    const { id } = useParams();
    const [searchParams] = useSearchParams();
    const { getDefaultParams } = useDefaultApiParams();
    const rumClient = useAnalyticsContext();
    const isEnabled = (_a = options === null || options === void 0 ? void 0 : options.enabled) !== null && _a !== void 0 ? _a : true;
    const { storefrontId, isValid, storeMeta } = resolveStorefront(id);
    const { storefrontCategories, invalidCategories } = parseCategories(storeMeta, searchParams);
    const firstCategory = storefrontCategories[0];
    const canFetch = storeMeta.type === 'DEALS' ? !!firstCategory : storeMeta.products.length > 0;
    // Analytics logging — fire only once on initial page load
    useEffect(() => {
        if (id && !isValid) {
            rumClient.recordEvent('UNKNOWN_STOREFRONT_ID', { requestedId: id, url: window.location.href });
        }
        if (invalidCategories.length > 0) {
            rumClient.recordEvent('INVALID_STOREFRONT_CATEGORIES', {
                storefrontId,
                invalidCategories: invalidCategories.join(','),
                url: window.location.href,
            });
        }
        if (storeMeta.type === 'DEALS' && storefrontCategories.length === 0) {
            rumClient.recordEvent('EMPTY_STOREFRONT_CATEGORIES', { storefrontId, url: window.location.href });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps  -- run only once on mount
    }, []);
    const result = useQuery({
        queryKey: storeMeta.type === 'DEALS'
            ? ['getStorefrontProducts', storeMeta.strategy, firstCategory]
            : ['getProductWidget', storeMeta.products],
        queryFn: () => {
            const params = getDefaultParams();
            if (storeMeta.type === 'DEALS') {
                return HandshakePainterServiceApiV1.getStorefrontProducts(Object.assign({ strategy: storeMeta.strategy, category: firstCategory }, params));
            }
            return HandshakePainterServiceApiV1.getProductWidget(Object.assign({ asins: storeMeta.products, placement: PlacementType.CustomersAlsoBought }, params));
        },
        enabled: isEnabled && canFetch,
        staleTime: Infinity,
    });
    return Object.assign(Object.assign({}, result), { storefrontId, storeMeta, storefrontCategories });
};
// Fetches products for a specific DEALS category tab.
export const useCategoryProducts = (storefrontId, category) => {
    const { getDefaultParams } = useDefaultApiParams();
    const { storeMeta } = resolveStorefront(storefrontId);
    const strategy = storeMeta.type === 'DEALS' ? storeMeta.strategy : undefined;
    return useQuery({
        queryKey: ['getStorefrontProducts', strategy, category],
        queryFn: () => HandshakePainterServiceApiV1.getStorefrontProducts(Object.assign({ strategy: strategy, category }, getDefaultParams())),
        enabled: !!strategy && !!category,
        staleTime: Infinity,
    });
};
